import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_RETIREMENT_URLS, CIVILITY_MONSIEUR_CODE } from '../../../const/AppConst';
import useAppState from '../../../context/AppContext';
import { useFetchNomenclature } from '../../../hooks/useFetchNomenclature';
import { isPropositionInDraft, isConfirmedContactProposition, calculOffersRetired } from '../../../utils/dataUtils';
import RecapElement from '../../recap/RecapElement';
import moment from 'moment';
import RecapInfoBancaireFormComponent from '../../recap/RecapInfoBancaireFormComponent';
import ButtonModifyRecapInfo from '../../common/ButtonModifyRecapInfo';
import { IBeneficiaryAdditionReq, IOptionCotisation, IRERequestDataOffers, relationType } from '../../../context/model';
import { formattedAttachmentOrganization, formattedNir, getPriceOfOffer } from '../../../utils/utils';
import useFetchRetiredGaranties from '../../../hooks/useFetchRetiredGaranties';
import LoaderModal from '../../../modals/common/LoaderModal';

interface InfoPersoRecapTemplateProps {
    modifyIdentityUrl: string;
    modifyContactUrl: string;
    modifyHealthInfoUrl: string;
    modifyAddressUrl: string;
    modifyInfoBankUrl: string
}

export default function REInfoPersoRecapTemplate({ modifyIdentityUrl, modifyContactUrl, modifyHealthInfoUrl, modifyAddressUrl, modifyInfoBankUrl }: Readonly<InfoPersoRecapTemplateProps>) {

  const {
    state: { 
      proposition, 
      updatedProposition, 
      jwtToken, 
    }, dispatch
  } = useAppState();
  const isBeneficiaryGuaranty =
   proposition?.configuration?.canBeAddedBeneficiaries 
   && proposition?.configuration?.hasOfferStep;
  
  const navigate = useNavigate();

  const [departmentLabel, setDepartmentLabel] = useState<string | null>();
  const [familySituationLabel, setFamilySituationLabel] = useState<string | null>();
  const [countryLabel, setCountryLabel] = useState<string | null>();

  const familySituationNomenclature = useFetchNomenclature("familySituation");
  const departmentNomenclature = useFetchNomenclature("department", true);
  const countryNomenclature = useFetchNomenclature("country", true);

  const {garanties} = useFetchRetiredGaranties();
  const [loading, setLoading] = useState<boolean>(false);
  const [offers, setOffers] = useState<IOptionCotisation[]>([]);
  const guarantyCode = garanties.find(e => e.code === updatedProposition!.offerCode);
  const offerPrice = guarantyCode ? getPriceOfOffer(offers, guarantyCode.code) : "0 €";
    
    useEffect(() => {
          const fetchTotal = async () => {
            if (jwtToken) { 
              setLoading(true);
              const token = jwtToken.accessToken;

              // mapping to take array of children from updatedPropo
              const childrenInfo = updatedProposition?.beneficiaries
                ?.filter(bene => bene.relationType === relationType.CHILD )
                ?.map(child =>{ 
                  return {
                    birthDate: child.contact.birthDate,
                    hasAlsaceMoselle: child.contact.alsaceMoselleRegime,
                    hasHandicap: child.contact.hasHandicap
                }});
              // check if having partner
              const partner:IBeneficiaryAdditionReq[]|undefined = updatedProposition?.beneficiaries
                ?.filter(bene => bene.relationType !== relationType.CHILD)
              // mapping to take object of partner
              const partnerInfo = partner?.length === 0
                ? null
                : {
                  birthDate: partner?.[0].contact.birthDate,
                  hasAlsaceModelle: partner?.[0].contact.alsaceMoselleRegime
              }

              const body = {
                principalMemberInfo: {
                  retiredDate: updatedProposition?.retirementDate,
                  birthDate: updatedProposition?.birthDate,
                  hasAlsaceMoselle: updatedProposition?.alsaceMoselleRegime
                },
                partnerInfo: partnerInfo,
                childrenInfo: childrenInfo,
                effectiveDate: updatedProposition?.effectiveDate,
                coveragesInfo: updatedProposition?.coveragesInfo
              } as IRERequestDataOffers;
              const result = await calculOffersRetired(token, body);
              setOffers(result);
              setLoading(false);
            }
          } 
      
          fetchTotal();
    }, []);

    useEffect(() => {
        if(familySituationNomenclature 
          && updatedProposition?.familySituationCode) {
              const situationFamilyLabel =  `${familySituationNomenclature.options?.find(
              el => el.code === updatedProposition?.familySituationCode)?.label}`;
              dispatch({
                type: 'UPDATE_UPDATED_PROPOSITION', 
                updatedProposition: {...updatedProposition, familySituationLabel: situationFamilyLabel}});

              setFamilySituationLabel(situationFamilyLabel);
        }

        if(departmentNomenclature 
          && updatedProposition?.departmentCode) {
        setDepartmentLabel(`${updatedProposition.departmentCode} - ${departmentNomenclature.options?.find(
          d => d.code === updatedProposition.departmentCode)?.label}`);
        }

        if(countryNomenclature && updatedProposition?.countryId){
        setCountryLabel(`${countryNomenclature.options?.find(el => el.code === updatedProposition?.countryId)?.label}`)
    }
    }, [familySituationNomenclature, departmentNomenclature, countryNomenclature]);
    return (            
      <form style={{width: '100%'}}>         
        <div className='form-row page-content'>          
          <div className='col-flex-4'>
              <div className="flex-grid bg-blanc shadow-noir p-md mb-lg recap-content">
                <div className="grid-flex wrapper-edition ">
                    <h2 className="card-title bleu mb-sm">
                        Mon identité
                    </h2>

                    <ButtonModifyRecapInfo 
                        isDisplayed={proposition 
                          && isPropositionInDraft(proposition) 
                          && (!isConfirmedContactProposition(proposition!) || !isBeneficiaryGuaranty)}
                        handleClick={ () => navigate({ 
                          pathname: modifyIdentityUrl, 
                          search: `?id=${proposition!.propositionId}`, 
                        }) }
                    />

                </div>  
                <div>
                    <div className="form-row">      
                        <div className="form-group mw-50">
                            <RecapElement 
                              id='civilityLabel' 
                              label='Civilité' 
                              value={updatedProposition?.civilityCode?.toString() === CIVILITY_MONSIEUR_CODE ? "Monsieur" : 'Madame'} />
                        </div>
                        <div className="form-group mw-50">
                          <RecapElement 
                            id='familySituationLabel' 
                            label='Situation familiale' 
                            value={`${familySituationLabel}`} />
                        </div>
                    </div>

                    <div className='form-row'>
                        <div className="form-group mw-50">
                          <RecapElement 
                              id='lastName' 
                              label='Nom' 
                              value={`${updatedProposition?.lastName}`} />
                        </div>
                        <div className="form-group mw-50">
                          <RecapElement 
                              id='firstName' 
                              label='Prénom' 
                              value={`${updatedProposition?.firstName}`} />
                        </div>
                    </div>

                    {updatedProposition?.birthName && 
                      <div className='form-row'>
                       <div className="form-group mw-50">
                          <RecapElement 
                              id='familyName' 
                              label='Nom de naissance' 
                              value={`${updatedProposition?.birthName}`} />
                        </div>
                      </div>
                    }

                    <div className="form-row">      
                        <div className="form-group mw-50">
                            <RecapElement 
                                id='birthDate' 
                                label='Date de naissance' 
                                value= {updatedProposition?.birthDate
                                 ? moment(updatedProposition?.birthDate).format("DD/MM/YYYY") 
                                 : ""} />
                          
                        </div>
                        <div className="form-group mw-50">
                            <RecapElement 
                                id='birthPlace' 
                                label='Lieu de naissance' 
                                value={`${updatedProposition!.birthPlace}`} />
                        </div> 
                    </div>

                    
                </div>
            </div>
            <div className="flex-grid bg-blanc shadow-noir p-md mb-lg long-recap-content">
              <div className="grid-flex wrapper-edition ">
                  <h2 className="card-title bleu mb-sm">
                      Mes informations de santé
                  </h2>

                  <ButtonModifyRecapInfo 
                      isDisplayed={proposition 
                        && isPropositionInDraft(proposition) 
                        && (!isConfirmedContactProposition(proposition) || !isBeneficiaryGuaranty)}
                      handleClick={ 
                        () => navigate({ 
                          pathname: modifyHealthInfoUrl, 
                          search: `?id=${proposition!.propositionId}`, 
                        }) }
                  />

              </div>  
              <div>
                  <div className="form-row">      
                      <div className="form-group mw-100">
                          <RecapElement 
                            id='nir' 
                            label='Numéro de sécurité sociale' 
                            value={`${updatedProposition!.nir ? formattedNir(updatedProposition!.nir) : ''} ${updatedProposition!.nirKey}`}  />
                      </div>
                  </div>
                  <div className='form-row'>
                    <div className="form-group mw-100">
                      <RecapElement 
                        id='regimeCode' 
                        label='Organisme de rattachement' 
                        value={`${updatedProposition!.attachmentOrganization 
                          ? formattedAttachmentOrganization(updatedProposition!.attachmentOrganization)
                          : ''} `} />
                    </div>
                  </div>
              </div> 
            </div>
            <div className="flex-grid bg-blanc shadow-noir p-md mt-lg recap-content">
         
                  <div className="grid-flex wrapper-edition ">
                  <h2 className="card-title bleu mb-sm">
                  {
                    (updatedProposition!.beneficiaries && updatedProposition!.beneficiaries.length > 0) ? "Ma cotisation pour ma famille" : "Ma cotisation"
                  }
                  </h2>
                  <ButtonModifyRecapInfo 
                      isDisplayed={
                        proposition 
                        && isPropositionInDraft(proposition)
                         && ( !isBeneficiaryGuaranty)}
                      handleClick={ () => navigate({ 
                        pathname: APP_RETIREMENT_URLS.guarantyPage, 
                        search: `?id=${proposition!.propositionId}`, }) }
                  />
                

              </div> 
                <div className='px-medium'>
                    <div className="p-xs " style={{border: '1px solid black', borderRadius: '20px'}}>
                        <p className="fw-medium fz-lg  ta-center">{guarantyCode?.titre}</p>
                        
                        <div className='m-x-lg  ta-center'>
                            <span className="border-bleu light full mb-xs"></span>
                        </div>
                        
                        <p className='bleu fw-medium ta-center fz-md'>Votre cotisation*</p>
                        <p className='ta-center mb-xs fz-sm'>(hors cotisation obligatoire)</p>
                  
                    {(updatedProposition!.beneficiaries 
                    && updatedProposition!.beneficiaries.length > 0) ? 
                        <div>
                        <div className='ta-center'>
                            <p className=' fz-lg fw-bold'>{offerPrice} / <span className='fz-sm fw-bold'>mois</span></p>
                        </div>
                    
      
                        </div>
                        :
                        <div className='ta-center m-y-md'>
                            <p className=''><span className='fz-xs pr-xs'>
                              Pour vous</span><span className='fz-lg fw-bold'>{offerPrice} /</span><span className='fz-sm fw-medium'>mois</span>
                            </p> 
                        </div>
                    }

                  <p className='fz-xs m-y-sm ta-center'>*Sur la base des informations déclarées</p>
                  
                </div>                                                                                                   
                    
                </div>
                </div>
     
        
          </div>
          
          <div className='col-flex-4'>
            
          <RecapInfoBancaireFormComponent modifyUrl={modifyInfoBankUrl}/>


            <div className="flex-grid bg-blanc shadow-noir p-md mb-lg long-recap-content">
              <div className="grid-flex wrapper-edition ">
                  <h2 className="card-title bleu mb-sm">
                      Mon adresse
                  </h2>
                  <ButtonModifyRecapInfo 
                        isDisplayed={
                          proposition 
                          && isPropositionInDraft(proposition) 
                          && (!isConfirmedContactProposition(proposition!) || !isBeneficiaryGuaranty)}
                        handleClick={ 
                          () => navigate({ 
                            pathname: modifyAddressUrl, 
                            search: `?id=${proposition!.propositionId}`, }) }
                  />
              </div>  
             
              {
                  updatedProposition?.protectedAddress ?
                  <div className="form-row">      
                      <div className="form-group mw-100 flex va-center jc-start">
                        <div>
                          <RecapElement 
                            id='department' 
                            label='Votre department' 
                            value={`${departmentLabel}`} />
                        </div>
                        <img src={process.env.REACT_APP_RESSOURCE_BASE_URL + `/img/picto_MGProtect_Localisation.svg`} alt='MGPROTECT Localisation' width={60}/>
                      </div>
                  </div>
                  :  
                  <div>
                    {updatedProposition!.additionalAddress &&
                      <div className='form-row'>
                        <div className="form-group mw-100">
                            <RecapElement 
                              id='additionalAddress' 
                              label='Complément du nom' 
                              value={`${updatedProposition!.additionalAddress}`} />
                        </div>
                      </div>
                    }

                    {updatedProposition!.buildingName &&
                      <div className='form-row'>
                        <div className="form-group mw-100">
                            <RecapElement 
                              id='buildingName' 
                              label='Résidence et bâtiment' 
                              value={`${updatedProposition!.buildingName}`} />
                        </div>
                      </div>
                    }

                    <div className="form-row">      
                      <div className="form-group mw-100">
                          <RecapElement 
                            id='address'
                            label='Numéro et libellé de la voie' 
                            value={`${updatedProposition!.address}`} />
                      </div>
                    </div>

                    {updatedProposition!.lieudit &&
                      <div className='form-row'>
                        <div className="form-group mw-100">
                            <RecapElement 
                              id='lieudit' 
                              label='Lieu-dit, Hameau, Commune, BP' 
                              value={`${updatedProposition!.lieudit}`} />
                        </div>
                      </div>
                    }

                    <div className='form-row'>
                      <div className="form-group mw-100">
                          <RecapElement
                             id='address' 
                             label='Ville et Code postal' 
                             value={`${updatedProposition!.postalCode} ${updatedProposition!.city}`} />
                      </div>
                    </div>

                    <div className='form-row'>
                      <div className="form-group mw-100">
                          <RecapElement 
                            id='country' 
                            label='Pays' 
                            value={`${countryLabel}`} />
                      </div>
                    </div>
                  </div> 
              }
        
            </div>
           
                <div className="flex-grid bg-blanc shadow-noir p-md mb-lg recap-content">
              <div className="grid-flex wrapper-edition ">
                  <h2 className="card-title bleu mb-sm">
                      Mes modalités de contact
                  </h2>

                  <ButtonModifyRecapInfo 
                        isDisplayed={proposition 
                          && isPropositionInDraft(proposition)
                          && (!isConfirmedContactProposition(proposition!) || !isBeneficiaryGuaranty)}
                        handleClick={ () => navigate({ 
                          pathname: modifyContactUrl, 
                          search: `?id=${proposition!.propositionId}`, }) }
                  />

              </div>  
              <div>
                  <div className="form-row">      
                      <div className="form-group mw-100">
                          <RecapElement 
                            id='email' 
                            label='Email' 
                            value={`${updatedProposition!.email}`} />
                      </div>
                  </div>
                  <div className='form-row'>
                      <div className="form-group mw-100">
                          <RecapElement 
                            id='phoneNumber' 
                            label='Téléphone' 
                            value={updatedProposition?.isForeignMobile ? 
                              updatedProposition!.phoneNumber :
                              `${updatedProposition!.phoneNumber?.replace(/[^\dA-Z]/g, '').replace(/(.{2})/g, '$1 ').trim()}`
                            }
                           />
                      </div>
                  </div>
              </div>          
            </div>
          </div>
        
        </div>
         {loading && 
         <LoaderModal hasMessage={false}/>}
      </form>
  )
}
